import React, { useEffect, useMemo, useState } from 'react';

import { Loader, MultiSelect, MultiSelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteAthlete from 'hooks/athlete/useAutoCompleteAthlete';
import { AthleteAutoComplete } from 'types';

export interface IAthleteMultiSelectAutoComplete extends AthleteAutoComplete {}

interface AthleteMultiSelectAutoCompleteProps
  extends Omit<
    MultiSelectProps,
    'data' | 'clearable' | 'searchable' | 'searchValue' | 'onSearchChange' | 'icon' | 'nothingFound'
  > {
  value?: string[];
  onChange?: (values: string[]) => void;
}

const AthleteMultiSelectAutoComplete = ({
  value,
  onChange,
  ...msProps
}: AthleteMultiSelectAutoCompleteProps): JSX.Element => {
  const [autocompleteData, setAutocompleteData] = useState<IAthleteMultiSelectAutoComplete[]>([]);
  const [athleteSearch, setAthleteSearch] = useState<string>('');
  const [debouncedAthleteSearchTerm] = useDebouncedValue<string>(athleteSearch, 250);
  const { data, isLoading, isSuccess } = useAutoCompleteAthlete({
    search: debouncedAthleteSearchTerm,
  });

  const handleSearchChange = (search: string): void => {
    setAthleteSearch(search);
  };

  useEffect(
    () => {
      if (isSuccess && data) {
        const results = data.data as IAthleteMultiSelectAutoComplete[];

        setAutocompleteData(prevItems => [
          ...prevItems.filter(i => value?.includes(i.athleteId)),
          ...results,
        ]);
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuccess, data],
  );

  const handleChange = (selectedItems: string[]): void => {
    if (onChange) {
      onChange(selectedItems);
    }
  };

  const formattedData = useMemo(
    () =>
      autocompleteData.map(athlete => ({
        value: athlete.athleteId,
        label: athlete.fullName,
      })),
    [autocompleteData],
  );

  return (
    <MultiSelect
      label='Select Athletes'
      placeholder='Select athletes'
      {...msProps}
      value={value}
      onChange={handleChange}
      data={formattedData}
      clearable
      searchable
      searchValue={athleteSearch}
      onSearchChange={handleSearchChange}
      icon={isLoading && <Loader size='xs' />}
      nothingFound='No athletes found'
    />
  );
};

export default AthleteMultiSelectAutoComplete;
